/**
 * Invite links (user-management.md, "Invites"): `{domain}/invite/CODE`, the
 * link Server settings copies. The web server sends that path to
 * `/?invite=CODE` (the app's files load from the root); both forms are read
 * here, once per page load, and taken off the address bar.
 *
 * Signed in, the link opens its channel (a voice channel is joined), joining
 * the server first when you are not in it; a bad or expired code says so.
 * Signed out, the sign-in card names the server and channel, sign-up comes
 * first, and the channel opens right after (the code waits in this tab).
 *
 * Console v2 (§3.4): while raid mode runs, joining by invite is paused for
 * everyone but staff; the link says "Invites are paused" instead of asking
 * to join (and the server's RAID_MODE_ACTIVE says the same).
 */
import { ApiErrorCode, ChannelType, isVoiceChannelType, type Invite } from '@neosocial/protocol';
import { useEffect, useState } from 'react';
import { joinVoiceFromUi } from '../hub/voiceUi';
import { tr, useT } from '../i18n';
import { api, isApiRequestError } from '../lib/api';
import { failToast, runAction } from '../mod/actions';
import { useConfig } from '../store/config';
import { useData } from '../store/data';
import { raidNow } from './ConfigNotices';
import { goHub } from '../store/ui';
import { Icon } from '../ui/kit';
import { confirmDialog, toast } from '../ui/overlay';

/** The server's own check (social.controller): letters, digits and dashes. */
const CODE = /^[A-Za-z0-9-]{2,32}$/;
const KEY = 'ns:invite';

let taken: string | null | undefined;
// true when this page load was the invite link itself (not a code kept in the tab)
let fromAddress = false;

function remembered(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

function remember(code: string | null): void {
  try {
    if (code) sessionStorage.setItem(KEY, code);
    else sessionStorage.removeItem(KEY);
  } catch {
    /* storage refused: the code lives in memory only */
  }
}

/** The invite code this page was opened with (or one still waiting in this tab). */
export function takeInviteLink(): string | null {
  if (taken !== undefined) return taken;
  let code: string | null = null;
  try {
    const url = new URL(window.location.href);
    const path = /^\/invite\/([^/?#]+)\/?$/.exec(url.pathname);
    const raw = (path ? decodeURIComponent(path[1]!) : url.searchParams.get('invite'))?.trim();
    // a malformed code still counts as a link opened: it is reported as invalid
    if (raw && raw.length <= 64) code = raw;
    if (path || url.searchParams.has('invite')) {
      url.searchParams.delete('invite');
      window.history.replaceState(
        window.history.state,
        '',
        (path ? '/' : url.pathname) + url.search + url.hash,
      );
    }
  } catch {
    /* no window (tests) or a strange URL */
  }
  if (code) remember(code);
  fromAddress = !!code;
  taken = code ?? remembered();
  return taken;
}

/**
 * Whether this page was opened with the invite link itself. A code only kept
 * in the tab (the visitor came back to the app later without using it) still
 * names the invite on the sign-in card, but no longer puts sign-up first: the
 * person may well be here to sign in.
 */
export function inviteLinkInAddress(): boolean {
  // used or forgotten since: no longer an open invite
  return !!takeInviteLink() && fromAddress;
}

/** The code once, for the one place that acts on it. */
function claimInvite(): string | null {
  const code = takeInviteLink();
  taken = null;
  return code;
}

function forget(): void {
  taken = null;
  remember(null);
}

/** Tests only. */
export function resetInviteLink(): void {
  taken = undefined;
  fromAddress = false;
}

/** Resolves true once `pred` holds (the data store changed), false after `ms`. */
function until(pred: () => boolean, ms: number): Promise<boolean> {
  if (pred()) return Promise.resolve(true);
  if (ms <= 0) return Promise.resolve(false);
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      stop();
      resolve(pred());
    }, ms);
    const stop = useData.subscribe(() => {
      if (!pred()) return;
      clearTimeout(timer);
      stop();
      resolve(true);
    });
  });
}

/** Opens an invite as a signed-in user. Every outcome shows something. */
export async function openInvite(code: string): Promise<void> {
  const { t } = tr();
  const invalid = () =>
    toast(t('ns.invite.invalid'), { icon: 'link', tone: 'danger', duration: 8000 });
  forget();
  if (!CODE.test(code)) return void invalid();
  let inv: Invite;
  try {
    inv = await api.um.invite(code);
  } catch (e) {
    if (isApiRequestError(e)) invalid();
    else toast(t('ns.invite.offline'), { icon: 'alert', tone: 'warn', duration: 8000 });
    return;
  }
  const server = inv.guild?.name ?? '';
  const gid = inv.guild?.id;
  const ch = inv.channel;
  let joined = false;
  const paused = () =>
    toast(t('gov.invite.paused'), { icon: 'shield', tone: 'warn', duration: 8000 });
  if (gid && !useData.getState().guilds[gid]) {
    // raid mode: staff still join (the config's exemption says who is staff)
    const cfg = useConfig.getState().config;
    if (raidNow(cfg) && !cfg?.maintenance.exempt) return void paused();
    const ok = await confirmDialog({
      title: t('ns.invite.joinTitle', { server }),
      body: ch ? t('ns.invite.joinBody', { channel: channelLabel(ch) }) : undefined,
      confirmLabel: t('ns.invite.join'),
      icon: 'link',
    });
    if (!ok) return;
    const r = await runAction(() => api.um.acceptInvite(code), { quiet: true });
    if (!r.ok) {
      // banned: say it is about you (the generic 40007 text speaks of "that person")
      if (isApiRequestError(r.error) && r.error.code === ApiErrorCode.USER_BANNED)
        toast(t('ns.invite.banned', { server }), { icon: 'lock', tone: 'danger', duration: 8000 });
      else if (isApiRequestError(r.error) && r.error.code === ApiErrorCode.RAID_MODE_ACTIVE)
        paused();
      else failToast(r.error);
      return;
    }
    joined = true;
    await until(() => !!useData.getState().guilds[gid], 8000);
  }
  if (!ch) return;
  // just joined: the channel list arrives over the gateway
  if (!(await until(() => !!useData.getState().channels[ch.id], joined ? 8000 : 0))) {
    toast(t('ns.invite.cantSee', { channel: ch.name ?? '' }), {
      icon: 'lock',
      tone: 'warn',
      duration: 8000,
    });
    return;
  }
  if (ch.type === ChannelType.GUILD_VOICE || ch.type === ChannelType.GUILD_STAGE_VOICE)
    void joinVoiceFromUi(ch.id);
  else goHub({ kind: 'channel', channelId: ch.id });
}

/** In the app (signed in): acts on a waiting invite once. */
export function InviteLinkHost() {
  useEffect(() => {
    const code = claimInvite();
    if (code) void openInvite(code);
  }, []);
  return null;
}

type CtxState =
  { kind: 'checking' } | { kind: 'ok'; invite: Invite } | { kind: 'invalid' } | { kind: 'offline' };

/**
 * A channel as an invite names it: "#general" for a text channel, the bare
 * name for a voice or stage channel, whose card shows a speaker instead
 * (Discord). QA 2026-10-28: the card read "Neosocial · #Lobby".
 */
function channelLabel(ch: NonNullable<Invite['channel']>): string {
  return (isVoiceChannelType(ch.type) ? '' : '#') + (ch.name ?? '');
}

/** On the sign-in card (signed out): which server and channel the link is for. */
export function InviteContext({ code }: { code: string }) {
  const { t } = useT();
  const [state, setState] = useState<CtxState>(() =>
    CODE.test(code) ? { kind: 'checking' } : { kind: 'invalid' },
  );
  useEffect(() => {
    if (!CODE.test(code)) return void forget();
    let live = true;
    api.um.invite(code).then(
      (invite) => live && setState({ kind: 'ok', invite }),
      (e: unknown) => {
        if (!live) return;
        // a bad code is not kept for after sign-in
        if (isApiRequestError(e)) forget();
        setState(isApiRequestError(e) ? { kind: 'invalid' } : { kind: 'offline' });
      },
    );
    return () => {
      live = false;
    };
  }, [code]);
  const inv = state.kind === 'ok' ? state.invite : null;
  const server = inv?.guild?.name ?? '';
  const voice = !!inv?.channel && isVoiceChannelType(inv.channel.type);
  const raid = raidNow(useConfig((c) => c.config));
  return (
    <div className="ns-invite-ctx nx-glass" role="status" data-state={state.kind}>
      <Icon name={state.kind === 'invalid' ? 'alert' : voice ? 'volume' : 'link'} />
      <div className="ns-invite-ctx-t">
        {state.kind === 'ok' ? (
          <>
            <b>
              {inv?.channel
                ? t('ns.invite.toChannel', { server, channel: channelLabel(inv.channel) })
                : t('ns.invite.to', { server })}
            </b>
            <span>{raid ? t('gov.invite.paused') : t('ns.invite.signIn')}</span>
          </>
        ) : state.kind === 'invalid' ? (
          <b>{t('ns.invite.invalid')}</b>
        ) : state.kind === 'offline' ? (
          <span>{t('ns.invite.offline')}</span>
        ) : (
          <span>{t('ns.invite.checking')}</span>
        )}
      </div>
    </div>
  );
}
